// ============================================================================
// INTERACTION HUB
// Unified interaction center for tour, feedback, and future AI chat
// Replaces separate FeedbackFAB and StatusOrb
// ============================================================================

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Sparkles, X, Lightbulb, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStore } from '@/lib/store';
import { useAppConfig } from '@/hooks/useAppConfig';
import { toast } from '@/hooks/use-toast';
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';
import { supabase } from '@/lib/supabase';

export function InteractionHub() {
  const { feedbackEnabled, loading } = useAppConfig();
  const discovery = useStore((state) => state.discovery);
  const setDiscoveryActive = useStore((state) => state.setDiscoveryActive);
  const setDiscoveryStep = useStore((state) => state.setDiscoveryStep);
  const resetDiscoveryProgress = useStore((state) => state.resetDiscoveryProgress);

  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<'feedback' | 'support'>('feedback');

  const handleTourClick = () => {
    if (!discovery.active) {
      setDiscoveryStep(0);
      resetDiscoveryProgress();
      setDiscoveryActive(true);
    } else {
      setDiscoveryActive(false);
    }
    setMenuOpen(false);
  };

  const handleFeedbackAction = async (tab: 'feedback' | 'support') => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to submit feedback or contact support.',
        variant: 'destructive',
      });
      return;
    }
    setDefaultTab(tab);
    setFeedbackDialogOpen(true);
    setMenuOpen(false);
  };

  const isAmber = discovery.discoveredSteps.length === 0;

  // Define menu items
  const menuItems = [
    {
      id: 'tour',
      label: discovery.active ? 'Close Guide' : 'Start Discovery Hub',
      icon: Sparkles,
      onClick: handleTourClick,
      color: 'text-amber-500',
    }
  ];

  // Add feedback options if enabled
  if (!loading && feedbackEnabled) {
    menuItems.push(
      {
        id: 'feature',
        label: 'Feature Request',
        icon: Lightbulb,
        onClick: () => handleFeedbackAction('feedback'),
        color: 'text-blue-500',
      },
      {
        id: 'support',
        label: 'Contact Support',
        icon: MessageCircle,
        onClick: () => handleFeedbackAction('support'),
        color: 'text-emerald-500', // Distinct color for support
      }
    );
  }

  return (
    <>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="fixed z-[100]"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
          right: 'calc(env(safe-area-inset-right, 0px) + 1.5rem)',
        }}
      >
        <div className="relative">
          {/* Main Hub Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-tour="interaction-hub"
            className={cn(
              'relative group flex items-center justify-center w-12 h-12 rounded-full transition-all duration-500',
              'bg-white dark:bg-zinc-900 border shadow-lg hover:shadow-xl',
              discovery.active
                ? 'border-amber-500 ring-4 ring-amber-500/20'
                : 'border-slate-200 dark:border-zinc-800'
            )}
          >
            {/* Hover glow */}
            <div
              className={cn(
                'absolute -inset-1 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-500',
                isAmber ? 'bg-amber-400' : 'bg-blue-400'
              )}
            />

            {/* Main Icon - Changes based on menu state and tour activity */}
            <motion.div
              animate={{ rotate: menuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {menuOpen ? (
                <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              ) : (
                <HelpCircle
                  className={cn(
                    'w-5 h-5 transition-transform duration-500 group-hover:scale-110',
                    discovery.active ? 'text-amber-500' : 'text-blue-500',
                    discovery.active && 'rotate-12'
                  )}
                />
              )}
            </motion.div>

            {/* Tooltip */}
            <div className="absolute right-full mr-4 px-3 py-1.5 rounded-lg bg-zinc-900 text-white text-xs font-medium opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap border border-white/10 shadow-2xl">
              Help & Feedback
            </div>
          </button>

          {/* Menu Popover */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-full right-0 mb-3 w-56 overflow-hidden rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-xl origin-bottom-right"
              >
                {/* Menu Items */}
                <div className="flex flex-col">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={item.onClick}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors duration-200 border-b border-slate-200 dark:border-zinc-800 last:border-0 text-left"
                      >
                        <div className={cn("p-1.5 rounded-md bg-slate-100 dark:bg-zinc-800", item.color.replace('text-', 'bg-').replace('500', '100 dark:bg-opacity-20'))}>
                          <Icon className={cn('w-4 h-4', item.color)} />
                        </div>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Footer Info */}
                <div className="px-4 py-2 bg-slate-50 dark:bg-zinc-800 text-[10px] text-slate-400 text-center font-medium uppercase tracking-wider">
                  Adaptive Endurance Help
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Backdrop to close menu */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[-1]"
                onClick={() => setMenuOpen(false)}
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        defaultTab={defaultTab}
      />
    </>
  );
}
